import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cookie from "js-cookie";
import debounce from "lodash/debounce";
import orderBy from "lodash/orderBy";
import Checkbox from "../../../../../components/Checkbox/Checkbox";
import { Button } from "../../../../../components/DesignSystem/Button/Button";
import FullWidthCard from "../../../../../components/FullWidthCard/FullWidthCard";
import ListingMarker from "../../../../../components/ListingMarker/ListingMarker";
import Map from "../../../../../components/MapContainer/MapContainer";
import MapControl from "../../../../../components/MapContainer/MapControl";
import MapTilerLogo from "../../../../../components/MapContainer/MapTilerLogo";
import { useAuth } from "../../../../../contexts/AuthContext";
import { canUseDOM } from "../../../../../utils/dom";
import { useApi } from "../../../context/ApiContext";
import { useHoveredItem } from "../../../context/HoveredItemContext";
import { useI18n } from "../../../context/I18nContext";
import { useTracker } from "../../../context/TrackerContext";
import ListingDetailsCard from "./ListingDetailsCard";

const propTypes = {
  bounds: PropTypes.array,
  event: PropTypes.func,
  getListingTitle: PropTypes.func.isRequired,
  getListingUrl: PropTypes.func.isRequired,
  hoveredListingGroupId: PropTypes.string,
  isAutomaticSearchDisabled: PropTypes.bool,
  isInitialLoad: PropTypes.bool,
  isMapView: PropTypes.bool,
  isSmallScreen: PropTypes.bool,
  listings: PropTypes.array.isRequired,
  mapTilerKey: PropTypes.string,
  numResults: PropTypes.number.isRequired,
  numTotalResults: PropTypes.number.isRequired,
  onClickListing: PropTypes.func.isRequired,
  onMapMove: PropTypes.func,
  query: PropTypes.object,
  searchThisArea: PropTypes.func,
  selectedListing: PropTypes.string,
  showMapTilerLogo: PropTypes.bool,
  updateUrl: PropTypes.func,
  setMapRef: PropTypes.func,
  mapMovingBecauseOfSearch: PropTypes.bool,
  category: PropTypes.object,
};

const ListingsMap = ({
  bounds,
  event,
  getListingTitle,
  getListingUrl,
  isAutomaticSearchDisabled,
  isMapView,
  isSmallScreen,
  listings,
  mapTilerKey,
  numResults,
  numTotalResults,
  onClickListing,
  onMapMove: onMapMoveCallback,
  query,
  selectedListing,
  showMapTilerLogo,
  updateUrl,
  searchThisArea: searchThisAreaCallback,
  setMapRef,
  mapMovingBecauseOfSearch,
  category,
}) => {
  const api = useApi();
  const { t, lang } = useI18n();
  const { tracker } = useTracker();
  const { user } = useAuth();

  const [searchWhenMoving, setSearchWhenMoving] = useState(
    canUseDOM() && cookie.get("searchWhenMoving") !== "false" && !isSmallScreen,
  );
  const [listingVisible, setListingVisible] = useState(selectedListing !== "");
  const [listingSemiVisible, setListingSemiVisible] = useState(false);
  const listingWithPopupRef = useRef();

  const onChangeSearchWhenMoving = (e) => {
    e.stopPropagation();
    const searchWhenMoving = e.target.checked;
    setSearchWhenMoving(searchWhenMoving);
    const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;
    cookie.set("searchWhenMoving", searchWhenMoving ? "true" : "false", {
      expires: new Date(Date.now() + ONE_MONTH),
    });
  };

  const onMapMove = debounce((bounds) => {
    onMapMoveCallback(bounds, searchWhenMoving);
  }, 380);

  const { hoveredItem, setHoveredItem } = useHoveredItem();

  const handleMouseEnterListing = (listing) => {
    setHoveredItem(listing.groupId);
  };
  const handleMouseLeaveListing = (listing) => {
    if (setHoveredItem && listing.groupId === hoveredItem) {
      setHoveredItem("");
    }
  };

  const onListingClose = () => {
    setListingVisible(false);
    setListingSemiVisible(false);
    delete query.listing;
    updateUrl(query);
  };

  const onListingClick = (listing) => {
    setListingVisible(true);
    setListingSemiVisible(false);
    query.listing = listing.groupId;
    updateUrl(query);
    setHoveredItem(listing.groupId);
  };

  const onMapMoveStart = () => {
    if (selectedListing && isSmallScreen) {
      setListingSemiVisible(true);
    }
  };

  const onClickDetailsCard = (e, groupId) => {
    tracker.events.trackListingOnMapClicked({
      userId: user?._id || null,
      groupId,
    });

    if (!listingSemiVisible) {
      event("go to listing details from list", groupId);
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    setListingVisible(true);
    setListingSemiVisible(false);

    onClickListing(groupId);
  };

  const getListingById = useCallback(
    (id) => {
      if (!id) return null;
      return listings.find((listing) => listing.groupId === id);
    },
    [listings],
  );

  const searchThisArea = () => {
    if (event) {
      event("SearchThisArea");
    }

    searchThisAreaCallback();
  };

  const trackListingCardViewOnMobileIfNeeded = useCallback(
    (selectedListing) => {
      const currentListingObject = selectedListing
        ? getListingById(selectedListing)
        : null;

      if (currentListingObject) {
        api.analytics.createListingViewedEvent(
          currentListingObject.groupId,
          "SEARCH_RESULTS_MAP",
        );
      }
    },
    [api.analytics, getListingById],
  );

  const initValuesRef = useRef({
    isMapView,
    isSmallScreen,
    selectedListing,
    trackListingCardViewOnMobileIfNeeded,
  });

  // componentDidMount
  useEffect(() => {
    const {
      isMapView,
      isSmallScreen,
      selectedListing,
      trackListingCardViewOnMobileIfNeeded,
    } = initValuesRef.current;

    if (query.listing && isSmallScreen && isMapView) {
      setListingVisible(true);
      setListingSemiVisible(false);

      // If the component gets mounted with a selectedListing track it
      if (selectedListing) {
        trackListingCardViewOnMobileIfNeeded(selectedListing);
      }
    }
  }, [query.listing]);

  // componentDidUpdate
  useEffect(() => {
    trackListingCardViewOnMobileIfNeeded(selectedListing);
  }, [selectedListing, trackListingCardViewOnMobileIfNeeded]);

  // This way, when listings overlap the cheapest one will be in front.
  // If prices are equal, listing with the highest score will be shown
  const sortedListings = orderBy(listings, ["price", "score"], ["desc", "asc"]);

  const listingWithPopup = listingWithPopupRef.current;
  if (
    listingWithPopup &&
    !sortedListings.some((l) => l.groupId === listingWithPopup.groupId)
  ) {
    sortedListings.push(listingWithPopup);
  }
  const currentListingObject = selectedListing
    ? getListingById(selectedListing)
    : null;

  const markers = sortedListings.map((listing) => {
    const coords = listing.address?.location
      ? Array.prototype.slice
          .call(listing.address.location.coordinates)
          .reverse()
      : [0, 0];

    return (
      <ListingMarker
        t={t}
        lang={lang}
        key={listing.groupId}
        highlight={hoveredItem === listing.groupId}
        selectedListing={selectedListing}
        category={category}
        onMouseEnter={() => handleMouseEnterListing(listing)}
        onMouseLeave={() => handleMouseLeaveListing(listing)}
        listing={listing}
        position={coords}
        isSmallScreen={isSmallScreen}
        onClick={() => {
          event("click listing on map", listing.groupId);
          if (isSmallScreen) {
            onListingClick(listing);
          }
          onClickListing(listing.groupId);
        }}
        onPopup={(listing) => {
          listingWithPopupRef.current = listing;
        }}
        event={event}
        getListingTitle={getListingTitle}
        getListingUrl={getListingUrl}
        tracker={tracker}
        userId={user?._id || null}
        trackable
        trackingCallback={() => {
          api.analytics.createListingViewedEvent(
            listing.groupId,
            "SEARCH_RESULTS_MAP",
          );
        }}
      />
    );
  });

  return (
    <React.Fragment>
      {isSmallScreen && isMapView && (
        <FullWidthCard
          onClose={onListingClose}
          visible={!!(listingVisible && !!currentListingObject)}
          semiVisible={!!(listingSemiVisible && !!currentListingObject)}
          event={event}
          hasAction
          onAction={searchThisArea}
          actionButtonLabel={t("searchThisArea")}
        >
          {currentListingObject && (
            <ListingDetailsCard
              t={t}
              lang={lang}
              listing={currentListingObject}
              onClick={(e) =>
                onClickDetailsCard(e, currentListingObject.groupId)
              }
            />
          )}
        </FullWidthCard>
      )}
      <Map
        setMapRef={setMapRef}
        className="Listings-map"
        markers={markers}
        minZoom={2}
        maxZoom={22}
        bounds={bounds}
        onMapMove={onMapMove}
        zoomControl={!isSmallScreen}
        mapTilerKey={mapTilerKey}
        isSmallScreen={isSmallScreen}
        mapMovingBecauseOfSearch={mapMovingBecauseOfSearch}
        isMapView={isMapView}
        eventHandlers={{
          movestart: onMapMoveStart,
        }}
      >
        {!isSmallScreen && (
          <React.Fragment>
            <MapControl position="bottomright">
              <div className="ListingsMap-resultsCounter">
                {numResults > 0
                  ? t("showingNumResults", {
                      numResults,
                      numTotalResults:
                        numTotalResults > 200 ? "200+" : numTotalResults,
                    })
                  : t("noResults")}
              </div>
            </MapControl>
            <MapControl position="topright">
              {isAutomaticSearchDisabled ? (
                <Button
                  className="Listings-map--ControlButton"
                  colorVariant="white"
                  onClick={searchThisArea}
                >
                  {t("searchThisArea")}
                </Button>
              ) : (
                <div className="ListingsMap-searchControl">
                  <Checkbox
                    label={t("searchWhenMoving")}
                    checked={searchWhenMoving}
                    onChange={onChangeSearchWhenMoving}
                  />
                </div>
              )}
            </MapControl>
          </React.Fragment>
        )}
        {showMapTilerLogo && <MapTilerLogo />}
      </Map>
    </React.Fragment>
  );
};

ListingsMap.propTypes = propTypes;

export default ListingsMap;
